import type { GeoCoords } from '@/core/data/location/location.interface';
import type { GetCoordinates, GetTableParams, LoadItemsData, Result } from '@/core/network/http/httpClient.interface';
import { useIntersectStore } from '@/stores/intersect.store';
import type { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import { GamStoreId } from '@/views/composables/constants/main/store.constants';
import { debounce } from 'lodash';
import { defineStore } from 'pinia';
import { ref, type Ref } from 'vue';

export const useTableStore = <T extends { id: string }>(listId: GamListId) =>
  defineStore(`${GamStoreId.TABLE}-${listId}`, () => {
    const intersectStore = useIntersectStore();
    const isLoading = ref<boolean>(false);
    const itemList = ref<T[] | null>(null) as Ref<T[] | null>;
    const selectedItem = ref<T | null>(null) as Ref<T | null>;
    const selectedIndex = ref<number>(0);
    const addMode = ref<boolean>(false);
    const detailsOpen = ref<boolean>(false);
    const tableParams = ref<GetTableParams>({
      afterCursor: null,
    });
    const loadCallback = ref<((listId: GamListId) => Promise<Result<T[]>>) | null>(null);
    const coordinates = ref<GetCoordinates | null>(null);
    let fetchGeneration = 0;

    const setAddMode = (state: boolean) => {
      addMode.value = state;
    };

    const setLoadCallback = (callback: (listId: GamListId) => Promise<Result<T[]>>) => {
      loadCallback.value = callback;
    };

    const setCursor = (cursor?: string | null): void => {
      tableParams.value.afterCursor = cursor;
    };

    const setSearchQuery = async (data?: LoadItemsData): Promise<void> => {
      tableParams.value.search = data?.search;
      if (!data?.preventLoad) {
        await loadItems(data);
      }
    };

    const setSearchQueryImmediate = async (data?: LoadItemsData): Promise<void> => {
      await setSearchQuery(data);
    };

    const setLocationParam = (location: GeoCoords | null): void => {
      if (location) {
        coordinates.value = {
          latitude: location.lat || undefined,
          longitude: location.lng || undefined,
        };
      } else {
        coordinates.value = null;
      }
    };

    const getLocationParam = (): GetCoordinates | null => {
      if (tableParams.value.search?.length) {
        return null;
      } else {
        return coordinates.value;
      }
    };

    const populateList = (items: T[] | null): void => {
      if (tableParams.value.afterCursor && itemList.value?.length && items) {
        itemList.value = itemList.value?.concat(items);
      } else {
        itemList.value = items;
      }
    };

    const fetchItems = async (resetListAfterLoad?: boolean, selectFirstItem?: boolean): Promise<void> => {
      const gen = ++fetchGeneration;
      isLoading.value = true;

      const result = await loadCallback.value?.(listId);

      if (gen !== fetchGeneration) return;

      if (result?.data) {
        if (resetListAfterLoad) resetList();

        populateList(result.data as T[]);

        if (!tableParams.value.afterCursor && selectFirstItem) selectItem(0);

        setCursor(result.cursor?.afterCursor);
      }

      intersectStore.setLoadState(listId, true);
      isLoading.value = false;
    };

    const setSelectedItem = (item: T | null, index: number): void => {
      selectedItem.value = item;
      selectedIndex.value = index;
      // TODO: Is this needed for mobile? It messes up isShowGambit
      // if (!item) detailsOpen.value = false;
    };

    const selectItem = (index: number) => {
      selectedIndex.value = index;
      selectedItem.value = itemList.value?.[index] || null;
      detailsOpen.value = true;
    };

    const loadItems = async (data?: LoadItemsData): Promise<void> => {
      intersectStore.setLoadState(listId, false);
      if (!data?.loadOnInit) resetList(data?.resetSelected);

      await fetchItems(data?.loadOnInit, data?.selectFirstItem);
    };

    const replaceItems = async (items: any[]): Promise<void> => {
      intersectStore.setLoadState(listId, false);

      itemList.value = items;

      intersectStore.setLoadState(listId, true);

      isLoading.value = false;
    };

    const infiniteLoad = async (): Promise<void> => {
      if (!tableParams.value.afterCursor) return;
      await fetchItems();
    };

    const resetList = (resetSelected?: boolean): void => {
      setCursor(null);
      populateList(null);
      if (resetSelected) setSelectedItem(null, 0);
    };

    const updateItem = (update: Partial<T>): T | null => {
      if (!itemList.value) return null;

      const itemIndex = itemList.value.findIndex((item) => item.id === update.id);
      if (itemIndex === -1) {
        return null;
      }

      const tempData = itemList.value[itemIndex];
      Object.entries(update).forEach(([key, value]) => {
        if (value === undefined) return;

        tempData[key as keyof T] = value;
      });

      return tempData;
    };

    const updateItems = (callback: (el: T) => T) => {
      if (!Array.isArray(itemList.value)) return;

      itemList.value = itemList.value.map((el) => callback(el));
    };

    const addItem = (item: T) => {
      itemList.value?.push(item);
    };

    const insertItem = (item: T) => {
      itemList.value?.splice(0, 0, item);
    };

    const removeItem = ({ index, id }: { index?: number; id?: string }) => {
      if (typeof index !== 'number' && typeof id !== 'string') return;

      if (typeof id === 'string') {
        index = itemList.value?.findIndex((item) => item.id === id);
      }

      if (index === undefined || index === -1) return;

      itemList.value?.splice(index, 1);
    };

    return {
      isLoading,
      itemList,
      selectedItem,
      selectedIndex,
      detailsOpen,
      addMode,
      setAddMode,
      tableParams,
      setCursor,
      setSearchQuery: debounce(setSearchQuery, 200, { leading: false }),
      setSearchQueryImmediate,
      fetchItems,
      setSelectedItem,
      selectItem,
      resetList,
      replaceItems,
      loadItems,
      infiniteLoad,
      setLoadCallback,
      setLocationParam,
      getLocationParam,
      addItem,
      insertItem,
      removeItem,
      updateItem,
      updateItems,
      coordinates,
    };
  });
